import { useEffect } from 'react';
import { isAndroid } from 'react-device-detect';

import { Link } from 'react-router-dom';
import { PageTemplate } from '@_widgets/common';
import { FolderListItem, FolderListItemSkeleton, LogoHeader } from '@_widgets/folder';

import { loginLogic } from '@_features/auth';
import {
	useFolderListOrderStore,
	useFolderListQuery,
	useFolderCreatePopup,
	folderToast,
} from '@_features/folder';
import { usePermissionPopupHandler, usePromotionPopupHandler } from '@_features/popup';

import { authApi } from '@_entities/auth';
import { FOLDER_CONSTANTS, folderApi } from '@_entities/content';
import { TERMS_CONSTANTS } from '@_entities/system';

import { Bridge } from '@_shared/config';
import { ForeLogoIcon, InstagramIcon, PenIcon, PlusIcon } from '@_shared/icons';

import { Banner } from '@_shared/ui';
import { Typography } from '@_shared/ui/Typography';

import * as S from './FolderListPage.style';

export function FolderListPage() {
	const { order, setOrder } = useFolderListOrderStore();
	const { data: folderList, isLoading } = useFolderListQuery(order);

	const { openFolderCreatePopup } = useFolderCreatePopup();

	function onOpenFolderCreatePopup() {
		folderApi.checkFolderListLimit().then(({ data: { canCreate } }) => {
			if (!canCreate) {
				folderToast().maxFolderCount();
			} else {
				openFolderCreatePopup();
			}
		});
	}

	usePromotionPopupHandler();
	usePermissionPopupHandler();

	useEffect(() => {
		new Bridge().postVersion();
		authApi.getUserUUID().then(({ data: { uuid } }) => {
			new Bridge().postUUID(uuid);
		});
	}, []);

	return (
		<PageTemplate header={<LogoHeader />} nav>
			<Banner />
			{/* root/Folder Page Header */}
			<S.FolderListContainer>
				<S.OrderedButtonContainer>
					<S.OrderedButton onClick={() => setOrder('alphabetical')}>
						<Typography
							type="title-mini"
							color={order === 'alphabetical' ? 'font-primary' : 'font-tertiary'}
						>
							가나다순
						</Typography>
					</S.OrderedButton>
					<Typography type="title-mini" color="font-quaternary">
						|
					</Typography>
					<S.OrderedButton onClick={() => setOrder('saved')}>
						<Typography
							type="title-mini"
							color={order === 'saved' ? 'font-primary' : 'font-tertiary'}
						>
							담은순
						</Typography>
					</S.OrderedButton>
					<Typography type="title-mini" color="font-quaternary">
						|
					</Typography>
					<S.OrderedButton onClick={() => setOrder('manual')}>
						<Typography
							type="title-mini"
							color={order === 'manual' ? 'font-primary' : 'font-tertiary'}
						>
							사용자 지정순
						</Typography>
					</S.OrderedButton>
					{loginLogic.isLogin() && (
						<Link to="/folder-list/manual">
							<PenIcon />
						</Link>
					)}
					{/* </S.OrderedButton> */}
				</S.OrderedButtonContainer>
				<S.FolderList>
					{isLoading ? (
						<>
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
							<FolderListItemSkeleton />
						</>
					) : (
						<>
							{folderList?.map((folder) => <FolderListItem {...folder} />)}
							{loginLogic.isLogin() &&
								folderList?.length &&
								folderList.length < FOLDER_CONSTANTS.MAX_FOLDER_COUNT && (
									<S.FolderCreateButton onClick={onOpenFolderCreatePopup}>
										<PlusIcon />
										<Typography type="title-medium" color="font-secondary">
											폴더 추가
										</Typography>
									</S.FolderCreateButton>
								)}
						</>
					)}
				</S.FolderList>
			</S.FolderListContainer>
			{/* root/Folder page Footer */}
			<S.Footer>
				<S.FooterTerms>
					<Typography
						type="label-large"
						color="font-tertiary"
						onClick={() => {
							window.location.href = TERMS_CONSTANTS.TERMS_OF_SERVICE;
						}}
					>
						이용약관
					</Typography>
					<Typography type="label-large" color="font-tertiary">
						|
					</Typography>
					<Typography
						type="label-large"
						color="font-tertiary"
						onClick={() => {
							window.location.href = '/terms/2';
						}}
					>
						개인정보처리방침
					</Typography>
				</S.FooterTerms>

				<S.FooterTitle>
					<Typography type="title-small" color="font-secondary">
						포레 (FOR:e) 사업자 정보
					</Typography>
					<ForeLogoIcon />
				</S.FooterTitle>
				<S.FooterListItem>
					<Typography type="label-large" color="font-tertiary">
						상호명
					</Typography>
					<Typography type="label-large" color="font-tertiary">
						포레 (FOR:e)
					</Typography>
				</S.FooterListItem>
				<S.FooterListItem>
					<Typography type="label-large" color="font-tertiary">
						대표자명
					</Typography>
					<Typography type="label-large" color="font-tertiary">
						최민석
					</Typography>
				</S.FooterListItem>
				<S.FooterListItem>
					<Typography type="label-large" color="font-tertiary">
						사업자 등록 번호
					</Typography>
					<Typography type="label-large" color="font-tertiary">
						338-60-00724
					</Typography>
				</S.FooterListItem>
				<S.FooterListItem>
					<Typography type="label-large" color="font-tertiary">
						주소
					</Typography>
					<Typography type="label-large" color="font-tertiary">
						부산광역시 영도구 대교동1가 59 Sea-side Complex Town 202호
					</Typography>
				</S.FooterListItem>
				<S.FooterListItem>
					<Typography type="label-large" color="font-tertiary">
						이메일
					</Typography>
					<Typography type="label-large" color="font-tertiary">
						fore.cnl@gmail.com
					</Typography>
				</S.FooterListItem>
				<S.FooterListItem>
					<Typography type="label-large" color="font-tertiary">
						SNS
					</Typography>
				</S.FooterListItem>
				<S.FooterListItem>
					<InstagramIcon
						onClick={() => {
							if (isAndroid) {
								window.location.replace(
									'intent://instagram.com/talkak.official/#Intent;scheme=https;package=com.instagram.android;end',
								);
							} else {
								window.location.replace(
									'https://www.instagram.com/talkak.official',
								);
							}
						}}
					/>
				</S.FooterListItem>
			</S.Footer>
		</PageTemplate>
	);
}
